// Дерево вариантов: children[0] — главная линия, children[1..] — варианты.
// Порт ядра дерева из app.js v3 (строки ~170–250), без UI и глобалов.
import { Chess } from 'chess.js';
import type { GameNode, NodePos } from './types';

export function posFullFen(pos: NodePos): string {
  return pos.fen || `${pos.placement} ${pos.turn} - - 0 1`;
}

export function nodeFromPos(pos: NodePos, parent: GameNode | null, fullmoveStart?: number): GameNode {
  const n: GameNode = {
    placement: pos.placement,
    turn: pos.turn,
    fen: posFullFen(pos),
    parent: parent || null,
    children: [],
    fwd: null,
    san: null,
    moverWhite: null,
    moveNo: null,
    fullmove: fullmoveStart || 1,
  };
  if (parent) {
    n.moverWhite = parent.turn === 'w';
    n.moveNo = parent.fullmove;
    n.fullmove = parent.turn === 'b' ? parent.fullmove + 1 : parent.fullmove;
    // для загруженных партий SAN уже известен; иначе выводим по полному FEN родителя
    n.san = pos.san != null ? pos.san : deriveSan(parent.fen, pos.placement);
  }
  return n;
}

/** [root, …, node] */
export function pathToRoot(node: GameNode): GameNode[] {
  const arr: GameNode[] = [];
  for (let n: GameNode | null = node; n; n = n.parent) arr.push(n);
  return arr.reverse();
}

/** Ходы главной линии (без корня). */
export function mainLineOf(root: GameNode | null): GameNode[] {
  const arr: GameNode[] = [];
  let n = root;
  while (n && n.children.length) {
    n = n.children[0]!;
    arr.push(n);
  }
  return arr;
}

/** Конец главной линии. */
export function mainLeaf(root: GameNode): GameNode {
  let n = root;
  while (n.children.length) n = n.children[0]!;
  return n;
}

export function isOnMainLine(node: GameNode): boolean {
  for (let n = node; n.parent; n = n.parent) {
    if (n.parent.children[0] !== n) return false;
  }
  return true;
}

/** Ребёнок с такой же позицией (не плодим дубликаты при повторе хода). */
export function childMatching(node: GameNode, pos: NodePos): GameNode | null {
  return node.children.find((c) => c.placement === pos.placement && c.turn === pos.turn) || null;
}

/** Добавить (или переиспользовать) ход как ребёнка. Новый — вариант, ничего не стирает. */
export function appendChild(node: GameNode, pos: NodePos): GameNode {
  let c = childMatching(node, pos);
  if (!c) {
    c = nodeFromPos(pos, node);
    node.children.push(c);
  }
  return c;
}

/** Запомнить путь «вперёд» от корня к узлу. */
export function rememberPath(node: GameNode): void {
  for (let n = node; n.parent; n = n.parent) n.parent.fwd = n;
}

/** Сделать ветку узла главной линией (по всей цепочке развилок). */
export function promoteToMainLine(node: GameNode): boolean {
  if (!node || !node.parent || isOnMainLine(node)) return false;
  for (let n = node; n.parent; n = n.parent) {
    const kids = n.parent.children;
    const idx = kids.indexOf(n);
    if (idx > 0) {
      kids.splice(idx, 1);
      kids.unshift(n);
    }
  }
  rememberPath(node);
  return true;
}

/** SAN хода между позициями (когда san неизвестен — ручной ход на доске). */
export function deriveSan(prevFen: string, curPlacement: string, curTurn: 'w' | 'b' | null = null): string | null {
  try {
    const base = new Chess();
    base.load(prevFen, { skipValidation: true });
    for (const m of base.moves({ verbose: true })) {
      const t = new Chess();
      t.load(prevFen, { skipValidation: true });
      try {
        t.move(m);
      } catch {
        continue;
      }
      const parts = t.fen().split(' ');
      if (parts[0] === curPlacement && (!curTurn || parts[1] === curTurn)) return m.san;
    }
  } catch {
    /* не вышло — вернём null */
  }
  return null;
}
